// Desafio Pessoal — UMA pergunta só: a meta de produção (R$) que o vendedor se
// compromete a fazer no mês. O realizado NÃO é digitado: vem das vendas
// confirmadas do próprio vendedor no mês (ver desafioProducao.ts). O banco
// continua guardando os valores em `respostas` (migration 79), agora com uma
// única chave — por isso o formato Record se mantém.

export type DesafioValores = Record<string, number | null | undefined>;

/** Única chave gravada em `desafio_pessoal.respostas`. */
export const DESAFIO_META_KEY = "producao_meta";

export const DESAFIO_PERGUNTA = "Objetivo pessoal do mês";

/**
 * Lê a meta do JSONB de respostas (null quando ainda não foi definida).
 *
 * Zero é um objetivo VÁLIDO e não a ausência de um. Quem entra de férias, sai
 * de licença ou está em transição assume zero conscientemente — e essa é uma
 * resposta diferente de "não respondi". Enquanto o corte era `> 0`, o vendedor
 * salvava zero e a tela continuava cobrando "Objetivo ainda não definido".
 *
 * Só o negativo é recusado: não existe comprometer-se a produzir menos que
 * nada.
 */
export function getMeta(respostas: DesafioValores | null | undefined): number | null {
  const raw = respostas?.[DESAFIO_META_KEY];
  return typeof raw === "number" && Number.isFinite(raw) && raw >= 0 ? raw : null;
}

/**
 * % de atingimento da meta = realizado / meta, saturado em 100. Superar a meta
 * não passa de 100%.
 *
 * Os dois casos de borda são opostos e não podem cair no mesmo return: meta
 * AUSENTE não tem percentual nenhum (0), meta ZERO já está cumprida pelo só
 * fato de existir (100) — quem assumiu zero e produziu zero fez exatamente o
 * que combinou, e mostrar 0% chamaria isso de fracasso.
 */
export function calcAtingimento(
  meta: number | null | undefined,
  realizado: number | null | undefined,
): number {
  if (meta == null) return 0;
  if (meta <= 0) return 100;
  const pct = ((realizado ?? 0) / meta) * 100;
  return Math.max(0, Math.min(Math.round(pct * 10) / 10, 100));
}

/** Quanto ainda falta em R$ para bater a meta (0 quando já bateu). */
export function faltaParaMeta(
  meta: number | null | undefined,
  realizado: number | null | undefined,
): number {
  if (!meta || meta <= 0) return 0;
  return Math.max(0, meta - (realizado ?? 0));
}

/** Faixa de cor do progresso (marca North): vermelho / bege / azul accent. */
export const DESAFIO_FAIXAS = {
  baixo: { max: 30, cor: "#DC2626", label: "0–30%" },
  medio: { max: 70, cor: "#C4AE90", label: "30–70%" },
  alto: { max: 100, cor: "#354660", label: "70–100%" },
} as const;

export function corPorProgresso(pct: number): string {
  if (pct < DESAFIO_FAIXAS.baixo.max) return DESAFIO_FAIXAS.baixo.cor;
  if (pct < DESAFIO_FAIXAS.medio.max) return DESAFIO_FAIXAS.medio.cor;
  return DESAFIO_FAIXAS.alto.cor;
}
